<template>
    <v-dialog
        persistent
        transition="dialog-top-transition"
        v-model="lockDialog"
        content-class="rounded-xl"
        max-width="550px"
    >
        <v-form v-model="valid" ref="form">
            <v-card rounded="xl">
                <!-- title -->
                <v-card-title class="pb-2">
                    {{$t('FinalAccountsPage.lock-financial-file-accounts')}}
                </v-card-title>

                <!-- stepper -->
                <v-card-text class="pb-0">
                    <bee-stepper
                        v-model="stepper"
                        :steps="3"
                        :stepsLabels="[
                            $t('FinalAccountsPage.lock-dialog.stepper-validate'), 
                            $t('FinalAccountsPage.lock-dialog.stepper-lock-accounts'), 
                            $t('FinalAccountsPage.lock-dialog.stepper-lock')
                        ]"
                        withLineBetween
                        stepersPosition="top"
                        :altLabels="false"
                        :editable="false"
                        :nonLinear="false"
                    >
                        <!-- checking -->
                        <template v-slot:stepperContent.step-1>
                            <!-- all-bonds-are-posted -->
                            <v-row class="align-center">
                                <v-col cols="5" class="py-1">
                                    <span style="font-size: .95rem">
                                        {{$t('FinalAccountsPage.lock-dialog.checking-all-bonds-are-posted')}}
                                    </span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="!isAllBondsPosted"
                                        buffer-value="0"
                                        reverse
                                        height="3"
                                        color="grey"
                                        stream
                                        :value="isAllBondsPosted? 100 : 0"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!isAllBondsPosted"
                                        color="primary"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else
                                        :color="isAllBondsPosted !== -1 ? 'postedColor': 'redDarken1'"
                                    >
                                        {{
                                            isAllBondsPosted !== -1
                                            ? 'mdi-check-circle-outline'
                                            : 'mdi-close-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>

                            <!-- all-invoices-are-posted -->
                            <v-row class="align-center">
                                <v-col cols="5" class="py-1">
                                    <span style="font-size: .95rem">
                                        {{$t('FinalAccountsPage.lock-dialog.checking-all-invoices-are-posted')}}
                                    </span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="!isAllInvoicesPosted"
                                        buffer-value="0"
                                        reverse
                                        height="3"
                                        color="grey"
                                        stream
                                        :value="isAllInvoicesPosted? 100 : 0"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!isAllInvoicesPosted"
                                        color="primary"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else
                                        :color="isAllInvoicesPosted !== -1 ? 'postedColor': 'redDarken1'"
                                    >
                                        {{
                                            isAllInvoicesPosted !== -1
                                            ? 'mdi-check-circle-outline'
                                            : 'mdi-close-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>

                            <!-- all-material-journals-are-posted -->
                            <v-row class="align-center">
                                <v-col cols="6" class="py-1">
                                    <span style="font-size: .95rem">
                                        {{$t('FinalAccountsPage.lock-dialog.checking-all-material-journals-are-posted')}}
                                    </span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="!isAllMaterialJournalsPosted"
                                        buffer-value="0"
                                        reverse
                                        height="3"
                                        color="grey"
                                        stream
                                        :value="isAllMaterialJournalsPosted? 100 : 0"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!isAllMaterialJournalsPosted"
                                        color="primary"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else
                                        :color="isAllMaterialJournalsPosted !== -1 ? 'postedColor': 'redDarken1'"
                                    >
                                        {{
                                            isAllMaterialJournalsPosted !== -1
                                            ? 'mdi-check-circle-outline'
                                            : 'mdi-close-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>

                            <!-- all-manufacturing-bonds-are-posted -->
                            <v-row class="align-center">
                                <v-col cols="6" class="py-1">
                                    <span style="font-size: .95rem">
                                        {{$t('FinalAccountsPage.lock-dialog.checking-all-manufacturing-bonds-are-posted')}}
                                    </span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="!isAllManufacturingBondsPosted"
                                        buffer-value="0"
                                        reverse
                                        height="3"
                                        color="grey"
                                        stream
                                        :value="isAllManufacturingBondsPosted? 100 : 0"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!isAllManufacturingBondsPosted"
                                        color="primary"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else
                                        :color="isAllManufacturingBondsPosted !== -1 ? 'postedColor': 'redDarken1'"
                                    >
                                        {{
                                            isAllManufacturingBondsPosted !== -1
                                            ? 'mdi-check-circle-outline'
                                            : 'mdi-close-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>

                            <!-- no-negative-values-for-materials -->
                            <v-row class="align-center">
                                <v-col cols="6" class="py-1">
                                    <span style="font-size: .95rem">
                                        {{$t('FinalAccountsPage.lock-dialog.checking-no-negative-values-for-materials')}}
                                    </span>
                                </v-col>
                                <v-col class="flex-grow-1 py-1 px-1">
                                    <v-progress-linear
                                        v-if="!isNoNigativeMaterials"
                                        buffer-value="0"
                                        reverse
                                        height="3"
                                        color="grey"
                                        stream
                                        :value="isNoNigativeMaterials? 100 : 0"
                                        class="flex-grow-1"
                                    />
                                </v-col>
                                <v-col cols="auto" class="d-flex justify-end align-center py-1" style="height: 40px">
                                    <v-progress-circular
                                        v-if="!isNoNigativeMaterials"
                                        color="primary"
                                        size="18"
                                        width="2"
                                        indeterminate
                                        class="me-1"
                                    />
                                    <v-icon
                                        v-else
                                        :color="isNoNigativeMaterials !== -1 ? 'postedColor': 'redDarken1'"
                                    >
                                        {{
                                            isNoNigativeMaterials !== -1
                                            ? 'mdi-check-circle-outline'
                                            : 'mdi-close-circle-outline'
                                        }}
                                    </v-icon>
                                </v-col>
                            </v-row>
                        </template>

                        <!-- lock accounts -->
                        <template v-slot:stepperContent.step-2>
                            <v-row class="justify-center mt-1 mb-2">
                                <!-- Retained Earnings Account -->
                                <template>
                                    <!-- title -->
                                    <v-col cols="12" class="py-0">
                                        <span class="d-block mb-1 text--primary mb-2" style="font-size: .95rem">
                                            {{$t('FinalAccountsPage.lock-dialog.the-profit-and-loss-account')}}:
                                        </span>
                                    </v-col>

                                    <!-- radios -->
                                    <v-col cols="12" class="py-0" v-if="!defaultRetainedEarningsAccount">
                                        <v-radio-group
                                        	v-model="accountRetainedEarningsMethod"
                                        	row
                                        	class="mt-0"
                                        >
                                            <v-radio
                                            	:label="$t('FinalAccountsPage.lock-dialog.radio-select')"
                                            	:value="0"
                                            	class="ms-0"
                                                @click="
                                                    newRetainedEarningsAccount.parent_id = null
                                                    newRetainedEarningsAccount.name = null
                                                    newRetainedEarningsAccount.inner_no = null
                                                "
                                            ></v-radio>
                                            <v-radio
                                            	:label="$t('FinalAccountsPage.lock-dialog.radio-create')"
                                            	:value="1"
                                                @click="retainedEarningsAccountId = null"
                                            ></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <!-- select account / default Retained Earnings Account -->
                                    <v-col v-if="accountRetainedEarningsMethod === 0" cols="12" class="py-0">
                                        <div class="d-flex">
                                            <bee-account-autocomplete
                                                v-model="retainedEarningsAccountId"
                                                :label="$t('FinalAccountsPage.lock-dialog.account')"
                                                context="financial_file"
                                                :action-name="actions.EDIT"
                                                :rules="[rules.required]"
                                                :items="budget_accounts"
                                                :readonly="defaultRetainedEarningsAccount"
                                                :insideTable="defaultRetainedEarningsAccount"
                                                @keydown-enter-prevent="increaseStepper"
                                            ></bee-account-autocomplete>
                                        </div>
                                        <span
                                            v-if="defaultRetainedEarningsAccount"
                                            class="secondary--text text--lighten-4"
                                            style="font-size: .75rem"
                                        >
                                            ({{$t('FinalAccountsPage.lock-dialog.it-is-set-in-financial-file-accounts')}})
                                        </span>
                                    </v-col>

                                    <!-- new Retained Earnings Account -->
                                    <v-col v-if="accountRetainedEarningsMethod === 1" cols="12" class="py-2">
                                        <v-row>
                                            <v-col cols="12" class="py-sm-0 py-2">
                                                <bee-account-autocomplete
                                                    context="financial_file"
                                                    :action-name="actions.EDIT"
                                                    :label="$t('FinalAccountsPage.lock-dialog.new-account-parent')"
                                                    v-model="newRetainedEarningsAccount.parent_id"
                                                    :items="configs.accounts.filter(c => c.type == 1)"
                                                    @change="setNewRetainedEarningsAccountNo"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-account-autocomplete>
                                            </v-col>
                                            <v-col sm="6" cols="12" class="py-2">
                                                <bee-text-field
                                                    context="financial_file"
                                                    :action-name="actions.EDIT"
                                                    :label="$t('FinalAccountsPage.lock-dialog.new-account-name')"
                                                    v-model="newRetainedEarningsAccount.name"
                                                    :rules="[rules.required]"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-text-field>
                                            </v-col>
                                            <v-col sm="6" cols="12" class="py-2">
                                                <bee-accounting-no-field 
                                                    ref="accountNum"
                                                    :prefix="newRetainedEarningsAccountPrefixNo"
                                                    :rules="[rules.required, notSameNoParent]"
                                                    v-model="newRetainedEarningsAccount.inner_no"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-accounting-no-field>
                                            </v-col>
                                        </v-row>

                                        <span class="d-block mt-2 grey--text text--darken-2" style="font-size: .8rem">
                                            {{$t('FinalAccountsPage.lock-dialog.account-create-note')}}
                                        </span>
                                    </v-col>
                                </template>

                                <!-- Ending Inventory Account -->
                                <template>
                                    <!-- title -->
                                    <v-col cols="12" class="pb-0">
                                        <span class="d-block mb-1 text--primary mb-2" style="font-size: .95rem">
                                            {{$t('FinalAccountsPage.lock-dialog.the-materials-end-of-term-account')}}:
                                        </span>
                                    </v-col>

                                    <!-- radios -->
                                    <v-col cols="12" class="py-0" v-if="!defaultEndingInventoryAccount">
                                        <v-radio-group
                                        	v-model="accountEndingInventoryAccountMethod"
                                        	row
                                        	class="mt-0"
                                        >
                                            <v-radio
                                            	:label="$t('FinalAccountsPage.lock-dialog.radio-select')"
                                            	:value="0"
                                            	class="ms-0"
                                                @click="
                                                    newEndingInventoryAccount.parent_id = null
                                                    newEndingInventoryAccount.name = null
                                                    newEndingInventoryAccount.inner_no = null
                                                "
                                            ></v-radio>
                                            <v-radio
                                            	:label="$t('FinalAccountsPage.lock-dialog.radio-create')"
                                            	:value="1"
                                                @click="endingInventoryAccountId = null"
                                            ></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <!-- select account / default Ending Inventory Account -->
                                    <v-col v-if="accountEndingInventoryAccountMethod === 0" cols="12" class="py-0">
                                        <div class="d-flex">
                                            <bee-account-autocomplete
                                                v-model="endingInventoryAccountId"
                                                :label="$t('FinalAccountsPage.lock-dialog.account')"
                                                context="financial_file"
                                                :action-name="actions.EDIT"
                                                :rules="[rules.required]"
                                                :items="budget_accounts"
                                                :readonly="defaultEndingInventoryAccount"
                                                :insideTable="defaultEndingInventoryAccount"
                                                @keydown-enter-prevent="increaseStepper"
                                            ></bee-account-autocomplete>
                                        </div>
                                        <span
                                            v-if="defaultEndingInventoryAccount"
                                            class="secondary--text text--lighten-4"
                                            style="font-size: .75rem"
                                        >
                                            ({{$t('FinalAccountsPage.lock-dialog.it-is-set-in-financial-file-accounts')}})
                                        </span>
                                    </v-col>

                                    <!-- new Ending Inventory Account -->
                                    <v-col v-if="accountEndingInventoryAccountMethod === 1" cols="12" class="py-2">
                                        <v-row class="algin-center">
                                            <v-col cols="12" class="py-sm-0 py-2">
                                                <bee-account-autocomplete
                                                    context="financial_file"
                                                    :action-name="actions.EDIT"
                                                    :label="$t('FinalAccountsPage.lock-dialog.new-account-parent')"
                                                    v-model="newEndingInventoryAccount.parent_id"
                                                    :items="configs.accounts.filter(c => c.type == 1)"
                                                    @change="setNewEndingInventoryAccountNo"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-account-autocomplete>
                                            </v-col>
                                            <v-col sm="6" cols="12" class="py-2">
                                                <bee-text-field
                                                    context="financial_file"
                                                    :action-name="actions.EDIT"
                                                    :label="$t('FinalAccountsPage.lock-dialog.new-account-name')"
                                                    v-model="newEndingInventoryAccount.name"
                                                    :rules="[rules.required]"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-text-field>
                                            </v-col>
                                            <v-col sm="6" cols="12" class="py-2">
                                                <bee-accounting-no-field 
                                                    ref="accountNum"
                                                    :prefix="newEndingInventoryAccountPrefixNo"
                                                    :rules="[rules.required, notSameNoParent]"
                                                    v-model="newEndingInventoryAccount.inner_no"
                                                    @keydown-enter-prevent="increaseStepper"
                                                ></bee-accounting-no-field>
                                            </v-col>
                                        </v-row>

                                        <span class="d-block mt-2 grey--text text--darken-2" style="font-size: .8rem">
                                            {{$t('FinalAccountsPage.lock-dialog.account-create-note')}}
                                        </span>
                                    </v-col>
                                </template>
                            </v-row>
                        </template>

                        <!-- lock -->
                        <template v-slot:stepperContent.step-3>
                            <div
                            	v-html="$t('FinalAccountsPage.lock-dialog.details')"
                                :style="{lineHeight: '25px'}"
                                class="px-5"
                            ></div>
                        </template>
                    </bee-stepper>
                </v-card-text>
                <v-card-actions class="px-5">
                    <v-btn
                        color="grey darken-1"
                        text
                        :disabled="stepper < 2 || loading"
                        @click="stepper--"
                    >
                        {{$t('FinalAccountsPage.previous')}}
                    </v-btn>
                    <v-btn
                        color="greenDarken1"
                        text
                        :disabled="btnDisable || loading"
                        :loading="loading"
                        @click="stepper !== 3 ? increaseStepper() : lockFinancialFile()"
                    >
                        {{stepper !== 3 ? $t('FinalAccountsPage.next') : $t('FinalAccountsPage.lock')}}
                    </v-btn>
                    <v-btn color="redDarken1" :disabled="loading" text @click="reset">
                        {{$t('FinalAccountsPage.cancel')}}
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import rules from "../../../../validation";
import actions from "../../../../permissions/actions-names.js";
import BeeStepper from '../../../../components/custom-components/toolkits/readonly/BeeStepper.vue'
import { accountConfigType, accountSuggestionType, bondType } from '@/helpers/enums';
export default {
    props: {
        value: { type: Boolean }
    },
    
    components: {
        BeeStepper
    },

    data() {
        return {
            valid: null,
            accountRetainedEarningsMethod: 0,
            accountEndingInventoryAccountMethod: 0,

            retainedEarningsAccountId: null,
			endingInventoryAccountId: null,

			createRetainedEarningsAccount: false,
			createEndingInventoryAccount: false,

			defaultRetainedEarningsAccount: false,
			defaultEndingInventoryAccount: false,

			newRetainedEarningsAccount:{
				parent_id: null,
				name: null,
				inner_no: null
			},
			newEndingInventoryAccount:{
				parent_id: null,
				name: null,
				inner_no: null
			},

			stepper: 1,
			isAllBondsPosted: false,
			isAllInvoicesPosted: false,
			isAllMaterialJournalsPosted: false,
			isAllManufacturingBondsPosted: false,
            isNoNigativeMaterials: false,

            loading: false,
            
            ...rules,
            actions,
        }
    },

    watch: {
        value() {
            if (this.value) {
                this.$store.dispatch('financialFiles/lockValidate', {
                    financialFileId: this.configs.financial_file.id
                })
                .then((data) => {
                    setTimeout(() => {
                        this.isAllBondsPosted = data.are_there_un_posted_bonds ? -1 : true;
                    }, 350)
                    setTimeout(() => {
                        this.isAllInvoicesPosted = data.are_there_any_un_posted_invoices ? -1 : true;
                    }, 650)
                    setTimeout(() => {
                        this.isAllMaterialJournalsPosted = data.are_there_any_un_posted_materialjournals ? -1 : true;
                    }, 950)
                    setTimeout(() => {
                        this.isAllManufacturingBondsPosted = data.are_there_any_un_posted_manufacturing_bonds ? -1 : true;
                    }, 1250)
                    setTimeout(() => {
                        this.isNoNigativeMaterials = data.are_there_any_negative_material_balances ? -1 : true;
                    }, 1550)
                })

                // get locking accounts values
                // checking if locking accounts is set by defaule else get suggest name for them
                if (this.default_financial_file_accounts.retained_earnings_id) {
                    this.defaultRetainedEarningsAccount = true
                }
                else {
                    this.$store.dispatch('accounts/getSuggestNameForEndingAccountingPeriod', {
                        type: accountSuggestionType.retainedEarnings
                    })
                    .then((data) => {
                        this.newRetainedEarningsAccount.name = data;
                    })
                }
                if (this.default_financial_file_accounts.ending_inventory_id) {
                    this.defaultEndingInventoryAccount = true
                }
                else {
                    this.$store.dispatch('accounts/getSuggestNameForEndingAccountingPeriod', {
                        type: accountSuggestionType.endingInventory
                    })
                    .then((data) => {
                        this.newEndingInventoryAccount.name = data;
                    })
                }

                this.retainedEarningsAccountId = this.default_financial_file_accounts.retained_earnings_id;
                this.endingInventoryAccountId = this.default_financial_file_accounts.ending_inventory_id;
            }
        }
    },

    computed: {
        ...mapState("configs", ["configs", "default_financial_file_accounts"]),
        
        budget_accounts() {
			return this.configs?.accounts?.filter(c => c.type == 2 && c.final_type == 4);
		},
        newRetainedEarningsAccountPrefixNo(){
			return this.configs.accounts
				.find(c => c.id == this.newRetainedEarningsAccount.parent_id)
				?.accounting_no.toString() ?? null
		},
		newEndingInventoryAccountPrefixNo(){
			return this.configs.accounts
				.find(c => c.id == this.newEndingInventoryAccount.parent_id)
				?.accounting_no.toString() ?? null
        },
        
        lockDialog: {
            get(){
                return this.value;
            },
            set(newVal){
                this.$emit('input', newVal)
            }
        },

        btnDisable(){
            if (this.stepper === 1) {
                return !(
                    this.isAllBondsPosted === true
                    && this.isAllInvoicesPosted === true
                    && this.isAllMaterialJournalsPosted === true
                    && this.isAllManufacturingBondsPosted === true
                    && this.isNoNigativeMaterials === true
                )
            }
            else if (
                (
                    (this.defaultRetainedEarningsAccount && this.defaultEndingInventoryAccount)
                    || (
                        this.newRetainedEarningsAccount.name
                        && this.newRetainedEarningsAccount.inner_no
                        && this.newEndingInventoryAccount.name
                        && this.newEndingInventoryAccount.inner_no
                    )
                    || (
                        this.newEndingInventoryAccount.name
                        && this.newEndingInventoryAccount.inner_no
                        && this.retainedEarningsAccountId
                    )
                    || (
                        this.newRetainedEarningsAccount.name
                        && this.newRetainedEarningsAccount.inner_no
                        && this.endingInventoryAccountId
                    )
                    || this.retainedEarningsAccountId && this.endingInventoryAccountId
                ) && this.valid
            ){
                return false;
            }
            else {
                return true
            }
        }
    },

    methods: {
        lockFinancialFile() {
			this.loading = true;

			this.$store.dispatch('financialFiles/lock', {
				financialFileId: this.configs.financial_file.id
			})
			.then(() => {
                this.$store.dispatch("configs/fetchAll");
                this.$store.dispatch("bonds/fetchAll", { bondType: bondType.accounting });
                this.$emit('financial-file-locked');
                this.$emit('input', false);
                this.reset();
            })
            .catch(() => {
                this.reset();
            })
			.finally(() => {
				this.loading = false;
			});
        },
        
        setNewRetainedEarningsAccountNo(){
            if (this.newRetainedEarningsAccount.parent_id) {
                this.$store.dispatch("accounts/GenerateNewAccountNo", this.newRetainedEarningsAccount.parent_id)
                .then(response => {
                    this.newRetainedEarningsAccount.inner_no = response;
                })
            }
            else {
                this.newRetainedEarningsAccount.inner_no = null;
            }
        },
        
		setNewEndingInventoryAccountNo(){
            if (this.newEndingInventoryAccount.parent_id) {
                this.$store.dispatch("accounts/GenerateNewAccountNo", this.newEndingInventoryAccount.parent_id)
                .then(response => {
                    this.newEndingInventoryAccount.inner_no = response;
                    if (
                        this.newRetainedEarningsAccount.inner_no
                        && this.newRetainedEarningsAccount.parent_id === this.newEndingInventoryAccount.parent_id
                    ) {
                        this.newEndingInventoryAccount.inner_no += 1;
                    }
                })
            }
            else {
                this.newEndingInventoryAccount.inner_no = null;
            }
        },

        notSameNoParent(val) {
            return !this.configs.accounts.filter(c => c.type == 1).map(c => c.accounting_no).includes(val) || this.$t('FinalAccountsPage.rotate-dialog.same-parent-no')
        },
        
        increaseStepper() {
            if (!this.btnDisable) {
                if (this.stepper === 2){
                    const promises = [];

                    if (!this.defaultRetainedEarningsAccount) {     
                        if (this.accountRetainedEarningsMethod === 0) {
                            promises.push(
                                this.$store.dispatch('configs/changeDefaultFinancialFileAccounts', {
                                    accountConfigType: accountConfigType.retainedEarnings,
                                    accountId: this.retainedEarningsAccountId
                                })
                            )
                        }
                        else if (this.accountRetainedEarningsMethod === 1) {
                            promises.push(
                                this.$store.dispatch('accounts/createDefaultFinancialFileAccount', {
                                    type: accountConfigType.retainedEarnings,
                                    name: this.newRetainedEarningsAccount.name,
                                    inner_no: this.newRetainedEarningsAccount.inner_no,
                                    parent_id: this.newRetainedEarningsAccount.parent_id,
                                    final_type: 4
                                })
                            )
                        }
                    }
                    if (!this.defaultEndingInventoryAccount) {
                        if (this.accountEndingInventoryAccountMethod === 0) {
                            promises.push(
                                this.$store.dispatch('configs/changeDefaultFinancialFileAccounts', {
                                    accountConfigType: accountConfigType.endingInventory,
                                    accountId: this.endingInventoryAccountId
                                })
                            )
                        }
                        else if (this.accountEndingInventoryAccountMethod === 1) {
                            promises.push(
                                this.$store.dispatch('accounts/createDefaultFinancialFileAccount', {
                                    type: accountConfigType.endingInventory,
                                    name: this.newEndingInventoryAccount.name,
                                    inner_no: this.newEndingInventoryAccount.inner_no,
                                    parent_id: this.newEndingInventoryAccount.parent_id,
                                    final_type: 4
                                })
                            )
                        }
                    }

                    this.loading = true;
                    Promise.all(promises)
                    .then(() => {
                        this.stepper++;

                        if (!this.defaultRetainedEarningsAccount || !this.defaultEndingInventoryAccount) {
                            this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts')
                            .then(() => {
                                this.retainedEarningsAccountId = this.default_financial_file_accounts.retained_earnings_id;
                                this.endingInventoryAccountId = this.default_financial_file_accounts.ending_inventory_id;
                            });
                        }

                        if (this.accountRetainedEarningsMethod === 1 || this.accountEndingInventoryAccountMethod === 1) {
                            this.$store.dispatch('configs/fetchAll');
                        }

                        this.accountRetainedEarningsMethod = 0;
                        this.accountEndingInventoryAccountMethod = 0;

                        this.defaultRetainedEarningsAccount = true;
                        this.defaultEndingInventoryAccount = true;
                    })
                    .finally(() => {
                        this.loading = false;
                    })
                }
                else {
                    this.stepper++;
                }
            }
        },

        reset() {
            this.lockDialog = false;
            this.stepper = 1;

            this.createRetainedEarningsAccount = false;
            this.createEndingInventoryAccount = false;

            this.newRetainedEarningsAccount.parent_id = null;
            this.newRetainedEarningsAccount.name = null;
            this.newRetainedEarningsAccount.inner_no = null;
                
            this.newEndingInventoryAccount.parent_id = null;
            this.newEndingInventoryAccount.name = null;
            this.newEndingInventoryAccount.inner_no = null;

            this.accountRetainedEarningsMethod = 0;
            this.accountEndingInventoryAccountMethod = 0;

            this.defaultRetainedEarningsAccount = false;
            this.defaultEndingInventoryAccount = false;

            this.isAllBondsPosted = false;
            this.isAllInvoicesPosted = false;
            this.isAllMaterialJournalsPosted = false;
            this.isAllManufacturingBondsPosted = false;
            this.isNoNigativeMaterials = false;
        }
    }
}
</script>

<style>

</style>